
import React, { useState, useEffect } from 'react';
import { UserRole, Language } from '../types';
import { TRANSLATIONS } from '../translations';
import { ShieldCheck, Rocket, Zap, BarChart3, Users, Wallet, ArrowRight, CircleDollarSign, Twitter, Facebook, Instagram, ChevronLeft, Music, Globe, Layers, Cpu, Box, Layout, Link as LinkIcon, Briefcase } from 'lucide-react';
import { LanguageDropdown } from './LanguageDropdown';

interface LandingViewProps {
  onEnterApp: (role: UserRole) => void;
  language: Language;
  onLanguageChange: (lang: Language) => void;
}

export const LandingView: React.FC<LandingViewProps> = ({ onEnterApp, language, onLanguageChange }) => {
  const t = TRANSLATIONS[language];
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  
  // Ticker animation helper
  const [tickerOffset, setTickerOffset] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
        setTickerOffset(prev => (prev > 1000 ? 0 : prev + 1));
    }, 50);
    return () => clearInterval(interval);
  }, []);

  const handleLogin = () => {
      if (selectedRole) {
          onEnterApp(selectedRole);
      }
  };

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white flex flex-col relative overflow-hidden font-sans selection:bg-indigo-500/30">
      
      {/* Background Grid & Gradients */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />
      <div className="absolute top-[-10%] left-[20%] w-[40vw] h-[40vw] bg-indigo-500/10 rounded-full blur-[120px] animate-pulse" />
      <div className="absolute bottom-[-10%] right-[10%] w-[30vw] h-[30vw] bg-emerald-500/10 rounded-full blur-[100px] animate-pulse delay-700" />

      {/* Live Activity Ticker (Marquee) */}
      <div className="w-full bg-[#0B0E14]/80 border-b border-white/5 backdrop-blur-md h-10 flex items-center overflow-hidden z-20">
          <div className="flex gap-16 whitespace-nowrap animate-marquee px-4 text-xs font-medium tracking-wide text-slate-400">
              <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span> {t.landingTicker1}</span>
              <span className="flex items-center gap-2 text-indigo-300"><Rocket size={10} /> {t.landingTicker2}</span>
              <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-blue-400"></span> {t.landingTicker3}</span>
              <span className="flex items-center gap-2 text-orange-300"><Users size={10} /> {t.landingTicker4}</span>
              {/* Duplicate for smooth loop */}
              <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span> {t.landingTicker1}</span>
              <span className="flex items-center gap-2 text-indigo-300"><Rocket size={10} /> {t.landingTicker2}</span>
              <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-blue-400"></span> {t.landingTicker3}</span>
          </div>
      </div>

      {/* Header */}
      <header className="px-6 md:px-12 py-6 flex justify-between items-center z-20">
        <div className="flex items-center gap-3 cursor-pointer group" onClick={() => setSelectedRole(null)}>
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/20 group-hover:shadow-indigo-500/40 transition-all duration-300">
            <ShieldCheck className="text-white w-6 h-6" />
          </div>
          <span className="text-xl font-bold tracking-tight text-white group-hover:text-indigo-200 transition-colors">LinkKol</span>
        </div>
        <LanguageDropdown currentLanguage={language} onChange={onLanguageChange} />
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-12 z-10 relative">
        
        {selectedRole ? (
            // --- LOGIN VIEW (Step 2) ---
            <div className="w-full max-w-md animate-in fade-in slide-in-from-right-8 duration-500">
                <button 
                    onClick={() => setSelectedRole(null)}
                    className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors group"
                >
                    <div className="p-1 rounded-full bg-slate-800 group-hover:bg-slate-700 transition-colors">
                        <ChevronLeft size={16} />
                    </div>
                    <span className="text-sm font-medium">Back</span>
                </button>

                <div className="text-center mb-10">
                    <div className={`w-24 h-24 mx-auto rounded-3xl flex items-center justify-center mb-6 border-2 shadow-[0_0_40px_-10px] ${
                        selectedRole === UserRole.PROJECT 
                            ? 'bg-slate-900 border-indigo-500/50 shadow-indigo-500/30' 
                            : 'bg-slate-900 border-emerald-500/50 shadow-emerald-500/30'
                    }`}>
                        {selectedRole === UserRole.PROJECT ? (
                            <Rocket className="w-12 h-12 text-indigo-400" />
                        ) : (
                            <Zap className="w-12 h-12 text-emerald-400" />
                        )}
                    </div>
                    <h2 className="text-3xl font-bold text-white mb-3">
                        {selectedRole === UserRole.PROJECT ? t.iAmProject : t.iAmKol}
                    </h2>
                    <p className="text-slate-400 text-sm">
                        {t.loginWith}
                    </p>
                </div>

                <div className="space-y-3">
                    <button onClick={handleLogin} className="w-full bg-white hover:bg-slate-100 text-slate-900 font-bold py-3.5 rounded-xl flex items-center justify-center gap-3 transition-all shadow-lg transform hover:-translate-y-0.5">
                        <svg viewBox="0 0 24 24" className="w-5 h-5" >
                            <path fill="#EA4335" d="M24 12.276c0-.887-.082-1.773-.245-2.658H12.273V14.7h6.636c-.327 1.637-1.309 3-2.618 3.873v3.164h4.145c2.455-2.291 3.818-5.727 3.818-9.462z" />
                            <path fill="#34A853" d="M12.273 24c3.273 0 6-1.091 8-2.945l-4.145-3.164c-1.091.764-2.509 1.145-3.855 1.145-2.945 0-5.455-2.018-6.327-4.745H1.855v3.273C3.927 21.6 7.855 24 12.273 24z" />
                            <path fill="#FBBC05" d="M5.945 14.291c-.218-.655-.327-1.364-.327-2.091 0-.727.109-1.436.327-2.091V6.836H1.855C.655 9.164 0 11.564 0 14.182c0 2.618.655 5.018 1.855 7.345l4.091-3.236z" />
                            <path fill="#4285F4" d="M12.273 4.909c1.745 0 3.273.6 4.582 1.855l3.273-3.273C17.945 1.418 15.273 0 12.273 0 7.855 0 3.927 2.4 1.855 6.836l4.091 3.273c.873-2.727 3.382-4.745 6.327-4.745z" />
                        </svg>
                        Google
                    </button>

                    <div className="grid grid-cols-2 gap-3">
                        <button onClick={handleLogin} className="w-full bg-[#1DA1F2]/10 hover:bg-[#1DA1F2]/20 text-[#1DA1F2] border border-[#1DA1F2]/30 font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 transition-all">
                            <Twitter size={20} fill="currentColor" />
                            Twitter
                        </button>
                        <button onClick={handleLogin} className="w-full bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 transition-all">
                            <Music size={20} />
                            TikTok
                        </button>
                    </div>
                    
                    <div className="flex items-center gap-4 py-4">
                        <div className="h-px bg-slate-800 flex-1"></div>
                        <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">Web3 Login</span>
                        <div className="h-px bg-slate-800 flex-1"></div>
                    </div>

                    <button onClick={handleLogin} className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-3 transition-all shadow-lg shadow-indigo-500/20 transform hover:-translate-y-0.5">
                        <Wallet size={20} />
                        {t.connectWallet}
                    </button>
                </div>
            </div>
        ) : (
            // --- ROLE SELECTION VIEW (Step 1) ---
            <>
                {/* Hero Copy */}
                <div className="text-center max-w-4xl mx-auto mb-16 space-y-6 animate-in fade-in slide-in-from-bottom-8 duration-700">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-[10px] font-bold uppercase tracking-widest mb-2 hover:bg-indigo-500/20 transition-colors cursor-default">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
                        </span>
                        Web3 Native Marketing Layer
                    </div>
                    <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.1]">
                        <span className="bg-clip-text text-transparent bg-gradient-to-b from-white via-white to-slate-400">
                            {t.landingTitle}
                        </span>
                    </h1>
                    <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed">
                        {t.landingSubtitle}
                    </p>
                </div>

                {/* Role Selection Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-5xl px-4 mb-24">
                
                {/* Project Card */}
                <div 
                    onClick={() => setSelectedRole(UserRole.PROJECT)}
                    className="group relative bg-[#0F1218] border border-white/5 hover:border-indigo-500/50 rounded-[2rem] p-8 cursor-pointer transition-all duration-300 hover:shadow-[0_0_50px_-12px_rgba(99,102,241,0.25)] overflow-hidden"
                >
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    
                    <div className="absolute top-8 right-8 p-3 rounded-full bg-slate-900 border border-white/5 text-slate-500 group-hover:text-indigo-400 group-hover:border-indigo-500/30 transition-all duration-300 group-hover:rotate-[-45deg]">
                        <ArrowRight size={20} />
                    </div>
                    
                    <div className="relative z-10">
                        <div className="w-14 h-14 bg-indigo-900/20 rounded-2xl flex items-center justify-center mb-6 border border-indigo-500/20 group-hover:scale-110 transition-transform duration-300">
                            <Rocket className="w-7 h-7 text-indigo-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-white mb-2">{t.iAmProject}</h2>
                        <p className="text-indigo-300 font-medium text-sm mb-6">{t.projectTagline}</p>
                        
                        <div className="space-y-3 pt-6 border-t border-white/5">
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500">Metric</span>
                                <span className="font-medium text-slate-300">ROI & On-chain Data</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500">Performance</span>
                                <span className="font-bold text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded">{t.projectStat}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* KOL Card */}
                <div 
                    onClick={() => setSelectedRole(UserRole.KOL)}
                    className="group relative bg-[#0F1218] border border-white/5 hover:border-emerald-500/50 rounded-[2rem] p-8 cursor-pointer transition-all duration-300 hover:shadow-[0_0_50px_-12px_rgba(16,185,129,0.25)] overflow-hidden"
                >
                    <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                    <div className="absolute top-8 right-8 p-3 rounded-full bg-slate-900 border border-white/5 text-slate-500 group-hover:text-emerald-400 group-hover:border-emerald-500/30 transition-all duration-300 group-hover:rotate-[-45deg]">
                        <ArrowRight size={20} />
                    </div>

                    <div className="relative z-10">
                        <div className="w-14 h-14 bg-emerald-900/20 rounded-2xl flex items-center justify-center mb-6 border border-emerald-500/20 group-hover:scale-110 transition-transform duration-300">
                            <Zap className="w-7 h-7 text-emerald-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-white mb-2">{t.iAmKol}</h2>
                        <p className="text-emerald-300 font-medium text-sm mb-6">{t.kolTagline}</p>

                        <div className="space-y-3 pt-6 border-t border-white/5">
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500">Metric</span>
                                <span className="font-medium text-slate-300">Earnings & Reputation</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500">Payout</span>
                                <span className="font-bold text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded">{t.kolStat}</span>
                            </div>
                        </div>
                    </div>
                </div>
                </div>

                {/* Features Section */}
                <div className="w-full max-w-7xl mx-auto px-6 mb-24">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {/* Feature 1 */}
                        <div className="bg-[#0F1218] border border-white/5 rounded-2xl p-6 hover:border-indigo-500/30 transition-colors">
                            <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4">
                                <BarChart3 className="text-blue-400" size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">{t.feature1Title}</h3>
                            <p className="text-slate-400 text-sm leading-relaxed">{t.feature1Desc}</p>
                        </div>

                        {/* Feature 2 */}
                        <div className="bg-[#0F1218] border border-white/5 rounded-2xl p-6 hover:border-emerald-500/30 transition-colors">
                            <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-4">
                                <ShieldCheck className="text-emerald-400" size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">{t.feature2Title}</h3>
                            <p className="text-slate-400 text-sm leading-relaxed">{t.feature2Desc}</p>
                        </div>

                        {/* Feature 3 */}
                        <div className="bg-[#0F1218] border border-white/5 rounded-2xl p-6 hover:border-purple-500/30 transition-colors">
                            <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center mb-4">
                                <CircleDollarSign className="text-purple-400" size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">{t.feature3Title}</h3>
                            <p className="text-slate-400 text-sm leading-relaxed">{t.feature3Desc}</p>
                        </div>
                    </div>
                </div>

                {/* How It Works Section */}
                <div className="w-full max-w-5xl mx-auto px-6 mb-24">
                    <h2 className="text-3xl font-bold text-center text-white mb-12">{t.howItWorks}</h2>
                    <div className="relative">
                        {/* Connecting Line (Desktop) */}
                        <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-indigo-500/50 to-transparent -translate-y-1/2 z-0"></div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                            {/* Step 1 */}
                            <div className="relative z-10 flex flex-col items-center text-center">
                                <div className="w-16 h-16 bg-[#0B0E14] border-2 border-indigo-500 rounded-full flex items-center justify-center mb-6 shadow-[0_0_20px_rgba(99,102,241,0.3)]">
                                    <LinkIcon size={24} className="text-indigo-400" />
                                </div>
                                <h3 className="text-xl font-bold text-white mb-2">{t.step1Title}</h3>
                                <p className="text-sm text-slate-400">{t.step1Desc}</p>
                            </div>

                            {/* Step 2 */}
                            <div className="relative z-10 flex flex-col items-center text-center">
                                <div className="w-16 h-16 bg-[#0B0E14] border-2 border-indigo-500 rounded-full flex items-center justify-center mb-6 shadow-[0_0_20px_rgba(99,102,241,0.3)]">
                                    <Briefcase size={24} className="text-indigo-400" />
                                </div>
                                <h3 className="text-xl font-bold text-white mb-2">{t.step2Title}</h3>
                                <p className="text-sm text-slate-400">{t.step2Desc}</p>
                            </div>

                            {/* Step 3 */}
                            <div className="relative z-10 flex flex-col items-center text-center">
                                <div className="w-16 h-16 bg-[#0B0E14] border-2 border-indigo-500 rounded-full flex items-center justify-center mb-6 shadow-[0_0_20px_rgba(99,102,241,0.3)]">
                                    <Wallet size={24} className="text-indigo-400" />
                                </div>
                                <h3 className="text-xl font-bold text-white mb-2">{t.step3Title}</h3>
                                <p className="text-sm text-slate-400">{t.step3Desc}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Ecosystem / Chains Strip - Redesigned */}
                <div className="w-full text-center max-w-7xl mx-auto px-6">
                    <p className="text-[10px] font-bold text-slate-600 uppercase tracking-[0.2em] mb-10">
                        {t.trustedBy}
                    </p>
                    <div className="flex flex-wrap justify-center items-center gap-x-12 gap-y-8">
                        {/* Solana */}
                        <div className="group flex items-center gap-2 opacity-40 hover:opacity-100 transition-all duration-300 cursor-default">
                            <Globe size={24} className="text-slate-300 group-hover:text-[#14F195] transition-colors" />
                            <span className="text-lg font-bold font-mono text-slate-300 group-hover:text-[#14F195] transition-colors group-hover:drop-shadow-[0_0_8px_rgba(20,241,149,0.5)]">SOLANA</span>
                        </div>
                        
                        {/* Ethereum */}
                        <div className="group flex items-center gap-2 opacity-40 hover:opacity-100 transition-all duration-300 cursor-default">
                            <Cpu size={24} className="text-slate-300 group-hover:text-[#627EEA] transition-colors" />
                            <span className="text-lg font-bold font-mono text-slate-300 group-hover:text-[#627EEA] transition-colors group-hover:drop-shadow-[0_0_8px_rgba(98,126,234,0.5)]">ETHEREUM</span>
                        </div>

                        {/* Binance */}
                        <div className="group flex items-center gap-2 opacity-40 hover:opacity-100 transition-all duration-300 cursor-default">
                            <Layers size={24} className="text-slate-300 group-hover:text-[#F3BA2F] transition-colors" />
                            <span className="text-lg font-bold font-mono text-slate-300 group-hover:text-[#F3BA2F] transition-colors group-hover:drop-shadow-[0_0_8px_rgba(243,186,47,0.5)]">BINANCE</span>
                        </div>

                        {/* Polygon */}
                        <div className="group flex items-center gap-2 opacity-40 hover:opacity-100 transition-all duration-300 cursor-default">
                            <Box size={24} className="text-slate-300 group-hover:text-[#8247E5] transition-colors" />
                            <span className="text-lg font-bold font-mono text-slate-300 group-hover:text-[#8247E5] transition-colors group-hover:drop-shadow-[0_0_8px_rgba(130,71,229,0.5)]">POLYGON</span>
                        </div>

                        {/* Arbitrum */}
                        <div className="group flex items-center gap-2 opacity-40 hover:opacity-100 transition-all duration-300 cursor-default">
                            <ShieldCheck size={24} className="text-slate-300 group-hover:text-[#2D374B] transition-colors" />
                            <span className="text-lg font-bold font-mono text-slate-300 group-hover:text-[#28A0F0] transition-colors group-hover:drop-shadow-[0_0_8px_rgba(40,160,240,0.5)]">ARBITRUM</span>
                        </div>
                    </div>
                </div>
            </>
        )}

      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 bg-[#0B0E14] py-8 mt-20 relative z-10">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-slate-600">
          <div>© 2024 LinkKol Protocol. Decentralized Marketing Layer.</div>
          <div className="flex gap-8">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_10px_#10b981]"></span>
              <span className="text-slate-400 font-mono">12,450+ {t.statsUsers}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-indigo-500 rounded-full shadow-[0_0_10px_#6366f1]"></span>
              <span className="text-slate-400 font-mono">$4.2M+ {t.statsVolume}</span>
            </div>
          </div>
        </div>
      </footer>
      
      {/* CSS for Marquee */}
      <style>{`
        @keyframes marquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
        }
        .animate-marquee {
            animation: marquee 40s linear infinite;
        }
      `}</style>
    </div>
  );
};
